import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const empty = {
  nome: "", cognome: "", codice_fiscale: "",
  data_nascita: "", luogo_nascita: "", indirizzo_residenza: "",
  telefono: "", email: "",
  documento_tipo: "", documento_numero: "", documento_scadenza: "",
  consenso_pubblicazione: false, consenso_gdpr: false,
  consenso_documento_note: "",
  provenienza: "", note: "",
};

const PROVENIENZA = [
  { v: "lead_pubblico", l: "Lead dal form pubblico" },
  { v: "agenzia", l: "Portato dall'agenzia" },
  { v: "diretto", l: "Contatto diretto" },
];
const DOC_TIPI = [
  { v: "carta_identita", l: "Carta d'identità" },
  { v: "patente", l: "Patente" },
  { v: "passaporto", l: "Passaporto" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

export default function SellersAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [fComune, setFComune] = useState("");

  const load = () => api.get("/sellers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const comuneOptions = useMemo(() => {
    const set = new Set(
      items
        .map((s) => (s.indirizzo_residenza || "").split(",").pop().trim())
        .filter((v) => v && v.length < 40)
    );
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((s) => {
      if (needle) {
        const hay = [s.nome, s.cognome, s.codice_fiscale, s.email, s.telefono].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (fComune && !(s.indirizzo_residenza || "").toLowerCase().includes(fComune.toLowerCase())) return false;
      return true;
    });
  }, [items, q, fComune]);

  const openView = async (id) => {
    try {
      const r = await api.get(`/sellers/${id}`);
      setEditing(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!editing.nome?.trim() || !editing.cognome?.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    try {
      const body = { ...editing };
      delete body.properties;
      if (editing.id) await api.put(`/sellers/${editing.id}`, body);
      else await api.post("/sellers", body);
      setEditing(null); load(); toast.success("Venditore salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Eliminare "${s.nome} ${s.cognome}"?`)) return;
    try { await api.delete(`/sellers/${s.id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="sellers-admin">
      <div className="flex justify-between items-end mb-8">
        <div><div className="label-uppercase">Anagrafica</div><h1 className="font-serif text-4xl mt-2">Venditori</h1></div>
        <button data-testid="new-seller-btn" onClick={() => setEditing({ ...empty })} className="btn-primary">Nuovo venditore</button>
      </div>

      <div className="mb-4 border border-[#E6E4E0] bg-white p-3 flex flex-wrap items-center gap-2" data-testid="sellers-filters">
        <input
          data-testid="sellers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca nome, cognome, codice fiscale, email…"
          className="admin-input flex-1 min-w-[280px] max-w-md"
        />
        <select data-testid="sellers-filter-comune" value={fComune} onChange={(e) => setFComune(e.target.value)} className="admin-input w-48">
          <option value="">Comune (tutti)</option>
          {comuneOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button
          onClick={() => { setQ(""); setFComune(""); }}
          disabled={!q && !fComune}
          className={`text-xs label-uppercase px-3 py-2 border ${(!q && !fComune) ? "border-[#E6E4E0] text-[#B8B4AD] cursor-not-allowed" : "border-[#7D2E3F] text-[#7D2E3F] hover:bg-[#FDFDFC]"}`}
        >
          Reset filtri
        </button>
        <div className="ml-auto text-xs text-[#5C5657]">{filtered.length} / {items.length}</div>
      </div>

      <table className="admin-table w-full bg-white border border-[#E6E4E0]">
        <thead><tr><th>Cognome e Nome</th><th>C.F.</th><th>Contatti</th><th title="Consenso alla pubblicazione dell'immobile">Consenso pubbl.</th><th title="Consenso al trattamento dati personali (GDPR)">Consenso GDPR</th><th>Immobili</th><th></th></tr></thead>
        <tbody>
          {filtered.map((s) => (
            <tr key={s.id} data-testid={`seller-row-${s.id}`}>
              <td>
                <button onClick={() => openView(s.id)} className="font-medium text-[#7D2E3F] hover:underline text-left">{s.cognome} {s.nome}</button>
                <div className="text-xs text-[#5C5657]">{s.indirizzo_residenza || "—"}</div>
              </td>
              <td className="font-mono text-xs whitespace-nowrap">{s.codice_fiscale || "—"}</td>
              <td className="text-xs">
                {s.email && <div>{s.email}</div>}
                {s.telefono && <div className="text-[#5C5657]">{s.telefono}</div>}
                {!s.email && !s.telefono && "—"}
              </td>
              <td>
                {s.consenso_pubblicazione ? (
                  <span className="badge-stamp badge-pronto" title={s.consenso_pubblicazione_at ? `Acquisito il ${fmtDate(s.consenso_pubblicazione_at)}` : ""} data-testid={`seller-consenso-${s.id}`}>Sì</span>
                ) : (
                  <span className="badge-stamp badge-criticita" data-testid={`seller-consenso-${s.id}`}>No</span>
                )}
              </td>
              <td>
                {s.consenso_gdpr ? (
                  <span className="badge-stamp badge-pronto" title={s.consenso_gdpr_at ? `Acquisito il ${fmtDate(s.consenso_gdpr_at)}` : ""} data-testid={`seller-gdpr-${s.id}`}>Sì</span>
                ) : (
                  <span className="badge-stamp badge-criticita" data-testid={`seller-gdpr-${s.id}`}>No</span>
                )}
              </td>
              <td className="text-xs">{s.properties_count || 0}</td>
              <td className="text-right space-x-3 whitespace-nowrap">
                <button onClick={() => openView(s.id)} className="text-xs label-uppercase text-[#7D2E3F]">Apri</button>
                <button onClick={() => del(s)} className="text-xs label-uppercase text-[#B3261E]">Elimina</button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[#5C5657]">Nessun venditore</td></tr>}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 bg-[#3A3D42]/40 flex items-start justify-center overflow-auto z-50 p-6" data-testid="seller-modal">
          <div className="bg-white border border-[#E6E4E0] w-full max-w-3xl p-8 my-8">
            <div className="flex justify-between items-start mb-6">
              <h2 className="font-serif text-2xl">{editing.id ? `${editing.cognome} ${editing.nome}` : "Nuovo venditore"}</h2>
              <button onClick={() => setEditing(null)} className="label-uppercase text-xs text-[#5C5657]">Chiudi ×</button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><label className="label-uppercase">Cognome *</label><input data-testid="seller-cognome" value={editing.cognome} onChange={(e) => setEditing({ ...editing, cognome: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Nome *</label><input data-testid="seller-nome" value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Codice fiscale</label><input value={editing.codice_fiscale || ""} onChange={(e) => setEditing({ ...editing, codice_fiscale: e.target.value.toUpperCase() })} className="admin-input font-mono uppercase" /></div>
              <div><label className="label-uppercase">Data di nascita</label><input type="date" value={editing.data_nascita || ""} onChange={(e) => setEditing({ ...editing, data_nascita: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Luogo di nascita</label><input value={editing.luogo_nascita || ""} onChange={(e) => setEditing({ ...editing, luogo_nascita: e.target.value })} className="admin-input" /></div>
              <div className="col-span-2"><label className="label-uppercase">Indirizzo di residenza</label><input value={editing.indirizzo_residenza || ""} onChange={(e) => setEditing({ ...editing, indirizzo_residenza: e.target.value })} className="admin-input" /></div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4 mt-2">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Contatti</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label-uppercase">Telefono</label><input value={editing.telefono || ""} onChange={(e) => setEditing({ ...editing, telefono: e.target.value })} className="admin-input" /></div>
                  <div><label className="label-uppercase">Email</label><input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="admin-input" /></div>
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Documento d'identità</div>
                <div className="grid grid-cols-3 gap-4">
                  <div><label className="label-uppercase">Tipo</label>
                    <select value={editing.documento_tipo || ""} onChange={(e) => setEditing({ ...editing, documento_tipo: e.target.value })} className="admin-input">
                      <option value="">—</option>
                      {DOC_TIPI.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                    </select></div>
                  <div><label className="label-uppercase">Numero</label><input value={editing.documento_numero || ""} onChange={(e) => setEditing({ ...editing, documento_numero: e.target.value })} className="admin-input" /></div>
                  <div><label className="label-uppercase">Scadenza</label><input type="date" value={editing.documento_scadenza || ""} onChange={(e) => setEditing({ ...editing, documento_scadenza: e.target.value })} className="admin-input" /></div>
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Consensi (GDPR)</div>
                <label className="flex items-start gap-3 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="seller-consenso-pub"
                    checked={!!editing.consenso_pubblicazione}
                    onChange={(e) => setEditing({ ...editing, consenso_pubblicazione: e.target.checked })}
                    className="mt-1"
                  />
                  <div className="text-sm">
                    <div>Consenso alla <strong>pubblicazione dell'immobile</strong> (foto, dati, esito, anche con criticità dichiarate)</div>
                    {editing.consenso_pubblicazione_at && (
                      <div className="text-xs text-[#5C5657] mt-1">Acquisito il {fmtDate(editing.consenso_pubblicazione_at)}</div>
                    )}
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.consenso_gdpr}
                    onChange={(e) => setEditing({ ...editing, consenso_gdpr: e.target.checked })}
                    className="mt-1"
                  />
                  <div className="text-sm">
                    <div>Consenso al <strong>trattamento dei dati personali</strong> (GDPR)</div>
                    {editing.consenso_gdpr_at && (
                      <div className="text-xs text-[#5C5657] mt-1">Acquisito il {fmtDate(editing.consenso_gdpr_at)}</div>
                    )}
                  </div>
                </label>
                <div className="mt-3">
                  <label className="label-uppercase">Riferimento documento firmato</label>
                  <input value={editing.consenso_documento_note || ""} onChange={(e) => setEditing({ ...editing, consenso_documento_note: e.target.value })} className="admin-input" placeholder="Es. modulo firmato del 12/03/2026, protocollato n. 456" />
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Provenienza & note</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label-uppercase">Come è arrivato</label>
                    <select value={editing.provenienza || ""} onChange={(e) => setEditing({ ...editing, provenienza: e.target.value })} className="admin-input">
                      <option value="">—</option>
                      {PROVENIENZA.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><label className="label-uppercase">Note interne</label><textarea rows={2} value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="admin-input" /></div>
                </div>
              </div>

              {editing.properties && editing.properties.length > 0 && (
                <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                  <div className="label-uppercase text-[#7D2E3F] mb-3">Immobili collegati ({editing.properties.length})</div>
                  <ul className="space-y-2 text-sm">
                    {editing.properties.map((pr) => (
                      <li key={pr.id} className="flex justify-between border-b border-[#F2EBEB] pb-2">
                        <div>
                          <Link to={`/admin/immobili/${pr.id}`} className="text-[#7D2E3F] hover:underline font-medium">{pr.titolo}</Link>
                          <div className="text-xs text-[#5C5657]">{pr.comune} · {pr.fascicolo_code || "—"}</div>
                        </div>
                        <div className="text-xs text-[#5C5657]">{pr.published ? "Online" : "—"}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-secondary text-xs">Annulla</button>
              <button data-testid="seller-save" onClick={save} className="btn-primary text-xs">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
